import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { ToastrNotificationService } from "../ui/toastr-notification.service";
import {
  ApiData,
  ApiMessage,
  ApiResponse,
} from "../../interfaces/api/api-response.interface";
import { map, Observable, tap } from "rxjs";
import { CommissionPlan, ReferalPercentage } from "../../interfaces/api/commission-plan.interface";
import { NgSelect } from "../../interfaces/ui/ui.interface";

@Injectable({
  providedIn: "root",
})
export class CommissionPlanService {
  public apiUrl = `${environment.apiUrl}/commission-plans`;

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService
  ) {}

  public getCommissionPlans(
    userId: string
  ): Observable<ApiResponse<ApiData<CommissionPlan[]>>> {
    const endpoint = `${this.apiUrl}/${userId}`;
    return this._httpClient.get<ApiResponse<ApiData<CommissionPlan[]>>>(
      endpoint
    );
  }

  public findAllCommissionPlans(
    filter: object
  ): Observable<ApiResponse<ApiData<CommissionPlan[]>>> {
    const endpoint = `${this.apiUrl}/find-all-commission-plans`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<CommissionPlan[]>>>(
      endpoint,
      { params }
    );
  }

  public findCommissionPlansForSelect(
    filter: object
  ): Observable<ApiData<NgSelect<string>[]>> {
    return this.findAllCommissionPlans(filter).pipe(
      map((response) => ({
        totalCount: response.data.totalCount,
        result: response.data.result.map((commissionPlan) => ({
          label: commissionPlan.planName,
          value: commissionPlan._id,
        })),
      }))
    );
  }
  public createCommissionPlan(
    request: CommissionPlan
  ): Observable<ApiResponse<ApiData<CommissionPlan>>> {
    const endpoint = `${this.apiUrl}/`;
    return this._httpClient
      .post<ApiResponse<ApiData<CommissionPlan>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public updateCommissionPlans(
    request: CommissionPlan
  ): Observable<ApiResponse<ApiData<CommissionPlan>>> {
    const endpoint = `${this.apiUrl}/`;
    return this._httpClient
      .put<ApiResponse<ApiData<CommissionPlan>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  private showNotification(message: ApiMessage): void {
    this._notificationService.showNotification({
      title: "tools.commissionPlans.title",
      message: message,
      type: "success",
    });
  }

  public getReferalPercentage(
      filter: object
    ): Observable<ApiResponse<ApiData<ReferalPercentage[]>>> {
      const endpoint = `${this.apiUrl}/get-referal-percentage`;
      const params = new HttpParams({ fromObject: { ...filter } });
      return this._httpClient.get<ApiResponse<ApiData<ReferalPercentage[]>>>(endpoint, {
        params,
      });
    }

    public createReferalPercentage(
      request: ReferalPercentage
    ): Observable<ApiResponse<ApiData<ReferalPercentage>>> {
      const endpoint = `${this.apiUrl}/create-referal-percentage`;
      return this._httpClient
        .post<ApiResponse<ApiData<ReferalPercentage>>>(endpoint, request)
        .pipe(tap((res) => this.showNotification(res.message)));
    }

    public updateReferalPercentage(
      request: ReferalPercentage
    ): Observable<ApiResponse<ApiData<ReferalPercentage>>> {
      const endpoint = `${this.apiUrl}/update-referal-percentage`;
      return this._httpClient
        .put<ApiResponse<ApiData<ReferalPercentage>>>(endpoint, request)
        .pipe(tap((res) => this.showNotification(res.message)));
    }

    public toggleReferalPercentageState(data: { _id: string; userId: string }): Observable<ApiResponse<ApiData<ReferalPercentage>>> {
      const endpoint = `${this.apiUrl}/toggle-referal-percentage-state`;
      return this._httpClient.put<ApiResponse<ApiData<ReferalPercentage>>>(endpoint, data).pipe(
        tap((res) => {
          this._notificationService.showNotification({
            title: 'tools.referalCommission.title',
            message: res.message,
            type: 'success',
          });
        })
      );
    }

    public getAllReferalPercentageBySelect(
      filter: object
    ): Observable<ApiData<NgSelect<string>[]>> {
      const endpoint = `${this.apiUrl}/referal-percentage-by-select`;
      const params = new HttpParams({ fromObject: { ...filter } });
    
      return this._httpClient
        .get<ApiResponse<ApiData<ReferalPercentage[]>>>(endpoint, { params })
        .pipe(
          map((response) => ({
            totalCount: response.data.totalCount,
            result: response.data.result.map((percentage) => ({
              label: `${percentage.percentage}%`,
              value: percentage.percentage.toString(),
            })),
          }))
        );
    }
    
}
